import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ignite-scale/artifacts/ij3ii683_file_00000000c58471f89c8c9afa889bdef4.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Industries', path: '/industries' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#0A0C1C]/85 border-b border-[#FF8C28]/15 shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="EmberCore Digital" className="h-24 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded-sm ${
                  location.pathname === link.path
                    ? 'text-[#FF8C28]'
                    : 'text-[#C8C8C8] hover:text-white'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-[#E65A14] to-[#FF8C28]"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center px-5 py-2.5 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_24px_rgba(230,90,20,0.5)] transition-all duration-300"
          >
            Get Started
          </Link>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl bg-[#0A0C1C]/95 border-b border-[#FF8C28]/15 overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={`block px-4 py-3 text-sm font-medium rounded-sm transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#FF8C28] bg-[#FF8C28]/10'
                      : 'text-[#C8C8C8] hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="block mt-3 px-4 py-3 text-sm font-bold text-center text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
